import { useDataContext } from "@/app/_context";
import BigPlayButton from "@/components/BigPlayButton";
import { Icons } from "@/components/icons/icons";
import { VideoLayout } from "@/components/VideoLayout";
import { frameSkipAmountMs, stepFrame } from "@/components/VideoLayout/Buttons";
import { cn } from "@/lib/utils";
import {
  MEDIA_KEY_SHORTCUTS,
  MediaPlayer,
  MediaProvider,
  Poster,
  useMediaState,
  type MediaKeyShortcuts,
  type MediaPlayerInstance,
} from "@vidstack/react";
import "@vidstack/react/player/styles/base.css";
import lodash from "lodash";
import { type ReactNode, useEffect, useMemo, useRef } from "react";

// , and . step a frame (as on YouTube), same as the control-bar buttons.
const frameStep =
  (direction: -1 | 1) =>
  ({ player }: { player: MediaPlayerInstance }) =>
    stepFrame(player, direction);

const keyShortcuts: MediaKeyShortcuts = {
  ...MEDIA_KEY_SHORTCUTS,
  frameBackward: { keys: ",", onKeyDown: frameStep(-1) },
  frameForward: { keys: ".", onKeyDown: frameStep(1) },
};

export function VmPlayer({
  portal,
  overlay,
}: {
  portal?: boolean;
  /** Drawn inside the player, above the video, so it stays in fullscreen. */
  overlay?: ReactNode;
}) {
  const {
    setPlayerInstance,
    setCurrentPlaybackTime,
    currentPlaybackTime,
    videoJsOptions,
    session,
    setSkipToTime,
    skipToTime,
    setIsPaused,
    setCurrentSessionIndex,
    currentSessionIndex,
    isPaused,
    setVideoCanPlay,
    isBusinessCardPlaying,
    setIsBusinessCardPlaying,
    currentPlaylist,
    setWasBusinessCardShown,
    wasBusinessCardShown,
    isPlayerWaiting,
    setIsPlayerWaiting,
    setIsSeeking,
    currentProjectInfo,
    consumePlayWhenReady,
  } = useDataContext();

  // The business card screen is just a player for the author's video bio.
  // With no bio URL it renders an empty <MediaPlayer src="">, so don't show it.
  const hasVideoBio =
    !!currentProjectInfo?.enableMerchandiseScreen &&
    (currentProjectInfo?.merchandiseVideoUrl?.length ?? 0) > 5;

  const { throttle } = lodash;

  const player = useRef<MediaPlayerInstance>(null);

  useEffect(() => {
    setPlayerInstance(player?.current);
  }, []);

  // The last time value that came from the player itself. Used to tell
  // player-driven updates apart from external seeks (frame step, timeline
  // click), which are the only ones allowed to write back into the player.
  const lastPlayerTimeRef = useRef<number | null>(null);

  const throttledSetCurrentTime = useMemo(
    () =>
      throttle((time: number) => {
        lastPlayerTimeRef.current = time;
        setCurrentPlaybackTime(time);
      }, frameSkipAmountMs),
    [],
  );

  const handleTimeUpdate = (e: { currentTime: number }) => {
    throttledSetCurrentTime(e.currentTime);
  };

  useEffect(() => {
    if (skipToTime && player?.current && skipToTime !== -1) {
      setIsPlayerWaiting(true);
      setIsPaused(false);
      player.current.currentTime = skipToTime / 1000;
      player.current.play()?.catch(() => {});
      setSkipToTime(-1);
    }
  }, [skipToTime, setSkipToTime]);

  useEffect(() => {
    if (!isPaused || !player?.current) return;

    // Never echo the player's own throttled timeupdate back at it — the value
    // is up to one throttle interval stale, so writing it back drags the
    // playhead backwards. Long full streams make the jump obvious.
    if (currentPlaybackTime === lastPlayerTimeRef.current) return;

    // Ignore sub-frame differences so we don't fight the player over rounding.
    if (
      Math.abs(player.current.currentTime - currentPlaybackTime) <
      frameSkipAmountMs / 2000
    ) {
      return;
    }

    player.current.currentTime = currentPlaybackTime;
  }, [currentPlaybackTime, isPaused]);

  useEffect(() => {
    if (isPaused) {
      if (player?.current) {
        player.current.pause();
      }
    }
  }, [isPaused]);

  useEffect(() => {
    if (currentSessionIndex === 1 && !wasBusinessCardShown && hasVideoBio) {
      setIsBusinessCardPlaying(true);
    }
  }, [currentSessionIndex, hasVideoBio]);

  const isEnded = useMediaState("ended", player);

  useEffect(() => {
    if (isBusinessCardPlaying && isEnded) {
      console.log("isBusinessCardPlaying && isEnded");
      setWasBusinessCardShown(true);
      setCurrentSessionIndex(currentPlaylist.length > 2 ? 1 : 0);
      setIsBusinessCardPlaying(false);
    }
  }, [isEnded]);

  if (videoJsOptions[0]?.src)
    return (
      <div className={cn("relative transition-opacity", portal && "h-full")}>
        <MediaPlayer
          className={cn(
            !portal
              ? "aspect-video"
              : "h-full flex items-center bg-red-500 absolute inset-0",
            "w-full group bg-slate-900 text-white font-sans overflow-hidden rounded-md ring-media-focus data-[focus]:ring-4",
          )}
          title={session?.title}
          src={videoJsOptions[0]?.src}
          logLevel="silent"
          crossOrigin
          playsInline
          autoPlay
          keyShortcuts={keyShortcuts}
          onCanPlay={() => {
            if (player.current) {
              setPlayerInstance(player.current);
              setVideoCanPlay(true);
              if (consumePlayWhenReady()) player.current.play()?.catch(() => {});
            }
          }}
          onWaiting={() => setIsPlayerWaiting(true)}
          onPlaying={() => {
            setIsPlayerWaiting(false);
            setIsSeeking(false);
          }}
          onSeeked={() => {
            setIsPlayerWaiting(false);
            setIsSeeking(false);
          }}
          onPosterChange={() => player.current && setVideoCanPlay(false)}
          onPause={() => setIsPaused(true)}
          onPlay={() => setIsPaused(false)}
          onTimeUpdate={handleTimeUpdate}
          ref={player}
        >
          <MediaProvider
            className={cn(
              isBusinessCardPlaying &&
                "[&>video]:-left-[31%] [&>video]:relative",
            )}
          >
            <Poster
              className={cn(
                portal
                  ? "aspect-video blur-sm w-full absolute opacity-0 transition-opacity data-[visible]:opacity-100"
                  : "absolute inset-0 block h-full w-full rounded-md opacity-0 transition-opacity data-[visible]:opacity-100 object-cover",
              )}
              src={session?.poster}
              alt={session?.title}
            />
          </MediaProvider>
          <BigPlayButton portal={portal!} />
          {isPlayerWaiting && (
            <div className="absolute inset-0 z-1000 flex place-items-center justify-center pointer-events-none">
              <Icons.spinner className="w-10 h-10 animate-spin text-white/80" />
            </div>
          )}
          <VideoLayout />
          {overlay}
        </MediaPlayer>
      </div>
    );
  else return;
}
